import React, { useEffect, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { useCategorias } from '../context/CategoriasContext';
import { crearProducto, generarCodigoProducto } from '../services/productosService';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro, limpiarTexto } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Crear un producto RÁPIDO desde la pantalla de Vender (sin foto), para venderlo
// de una cuando la clienta lleva algo que no estaba registrado. Queda como
// producto normal → alimenta categoría, costo recuperado, canal, fecha, etc.
export default function ProductoRapidoModal({ onCerrar, onCreado }) {
  const { categorias } = useCategorias();
  const { mostrarToast } = useToast();
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('');
  const [talla, setTalla] = useState('');
  const [marca, setMarca] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [costo, setCosto] = useState('');
  const [precio, setPrecio] = useState('');
  const [codigoPreview, setCodigoPreview] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Muestra el código que se le asignará al elegir categoría.
  useEffect(() => {
    let activo = true;
    if (!categoria) { setCodigoPreview(''); return; }
    generarCodigoProducto(categoria).then((c) => { if (activo) setCodigoPreview(c); }).catch(() => {});
    return () => { activo = false; };
  }, [categoria]);

  const cantidadNum = Math.max(Math.trunc(numeroSeguro(cantidad)), 0);
  const precioNum = numeroSeguro(precio);
  const costoNum = numeroSeguro(costo);
  const ganancia = precioNum - costoNum;
  const puedeGuardar = nombre.trim() && categoria && cantidadNum >= 1 && precioNum > 0 && precioNum >= costoNum && !guardando;

  // cerrar=true cierra el modal; cerrar=false lo deja abierto para crear otro
  // producto de la misma venta (mantiene la categoría elegida).
  const guardar = async (cerrar = true) => {
    if (!puedeGuardar) {
      if (precioNum > 0 && precioNum < costoNum) mostrarToast('El precio de venta no puede ser menor al costo', 'error');
      else mostrarToast('Completa nombre, categoría, cantidad y precio', 'error');
      return;
    }
    setGuardando(true);
    try {
      const creado = await crearProducto({
        nombre: limpiarTexto(nombre, 120).trim(),
        categoria,
        talla: limpiarTexto(talla, 30).trim() || null,
        marca: limpiarTexto(marca, 60).trim() || null,
        subtipo: null,
        descripcion: null,
        stock: cantidadNum,
        precio_costo: costoNum,
        precio_venta: precioNum,
      });
      mostrarToast(`${creado.codigo} agregado al carrito 🛒`, 'exito');
      onCreado(creado, cantidadNum);
      if (cerrar) {
        onCerrar();
      } else {
        // Limpia para el siguiente producto (conserva la categoría).
        setNombre(''); setTalla(''); setMarca(''); setCantidad('1'); setCosto(''); setPrecio('');
      }
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const label = { fontSize: '12px', color: '#6B7280', fontWeight: 600, display: 'block', marginBottom: '4px' };

  return (
    <div onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1200 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#FFF5F7', borderRadius: '16px 16px 0 0' }}>
          <h3 style={{ margin: 0, color: 'var(--color-primario,#DB2777)', fontSize: '18px' }}>➕ Producto nuevo (rápido)</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
            Para vender algo que no estaba en el inventario. Sin foto (la agregas después). Suma a la categoría y a tus reportes. ¿Varios productos? Usa <strong>"Crear y agregar otro"</strong> y al final cobrás todo junto (con POS si aplica).
          </p>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={label}>Nombre del producto *</label>
            <input type="text" value={nombre} autoFocus disabled={guardando}
              onChange={(e) => setNombre(e.target.value)} style={input} placeholder="Ej: Camiseta blanca / Pantie encaje" />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={label}>Categoría *</label>
            <select value={categoria} disabled={guardando} onChange={(e) => setCategoria(e.target.value)} style={input}>
              <option value="">Selecciona una categoría</option>
              {categorias.map((c) => <option key={c.nombre} value={c.nombre}>{c.icono} {c.nombre}</option>)}
            </select>
            {codigoPreview && (
              <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#065F46' }}>🏷️ Código: <strong>{codigoPreview}</strong></p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <div>
              <label style={label}>Talla (opcional)</label>
              <input type="text" value={talla} disabled={guardando}
                onChange={(e) => setTalla(e.target.value)} style={input} placeholder="Ej: M / 34B" />
            </div>
            <div>
              <label style={label}>Marca (opcional)</label>
              <input type="text" value={marca} disabled={guardando}
                onChange={(e) => setMarca(e.target.value)} style={input} placeholder="Ej: Leonisa" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <div>
              <label style={label}>Cantidad *</label>
              <CampoNumero valor={cantidad} onChange={setCantidad} deshabilitado={guardando}
                decimales={0} min={1} max={10000} minSiVacio estilo={input} />
            </div>
            <div>
              <label style={label}>Costo (L)</label>
              <CampoNumero valor={costo} onChange={setCosto} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
            </div>
            <div>
              <label style={label}>Precio venta (L) *</label>
              <CampoNumero valor={precio} onChange={setPrecio} deshabilitado={guardando}
                min={0} prefijo="L" placeholder="0.00" estilo={input} />
            </div>
          </div>

          {costoNum > 0 && precioNum > 0 && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: ganancia >= 0 ? '#F0FDF4' : '#FEF2F2', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', border: `1px solid ${ganancia >= 0 ? '#BBF7D0' : '#FECACA'}` }}>
              <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>📈 Ganancia c/u</span>
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: ganancia >= 0 ? 'var(--k-verde)' : '#DC2626' }}>
                {fmt(ganancia)}{costoNum > 0 ? `  ·  ${Math.round((ganancia / costoNum) * 100)}%` : ''}
              </span>
            </div>
          )}

          <button onClick={() => guardar(true)} disabled={!puedeGuardar}
            style={{ width: '100%', padding: '13px', backgroundColor: puedeGuardar ? 'var(--k-verde)' : '#9CA3AF', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: puedeGuardar ? 'pointer' : 'not-allowed', marginBottom: '8px' }}>
            {guardando ? '⏳ Creando...' : '🛒 Agregar al carrito y cerrar'}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onCerrar} disabled={guardando}
              style={{ flex: '0 0 auto', padding: '11px 16px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '10px', cursor: 'pointer', color: '#6B7280', fontWeight: 600 }}>
              Cancelar
            </button>
            <button onClick={() => guardar(false)} disabled={!puedeGuardar}
              title="Agrega este al carrito y deja la ventana abierta para crear otro producto de la misma venta"
              style={{ flex: 1, padding: '11px', backgroundColor: 'white', color: puedeGuardar ? 'var(--color-primario,#DB2777)' : '#9CA3AF', border: `1px solid ${puedeGuardar ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`, borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: puedeGuardar ? 'pointer' : 'not-allowed' }}>
              ➕ Crear y agregar otro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
